/**
 * Finance overview wire types + fetcher.
 *
 * BE source: ``ando-agent/src/api/v1/endpoints/finance.py::get_finance_overview``
 * + ``src/services/finance/overview.py::compute_overview``.
 *
 * The BE composite endpoint prefers ``bank_transactions`` (open-banking
 * connector) and falls back to FiC invoices via the canonical
 * ``FattureInCloudConnector.read_data`` path. When no connector is
 * connected the BE returns 200 with a zeroed payload + ``coverage_pct=0``
 * — the FE renders the empty state, NOT a fake number.
 *
 * ``X-Refresh-Cache: 1`` request header bypasses the 5-minute Redis cache.
 */

import { apiClient } from "../ApiClient.js";

/** One cash-flow bucket — Monday-anchored week. Amounts in tenant currency. */
export interface CashFlowBucket {
  /** ISO date of the Monday that starts the week (e.g. ``"2026-05-04"``). */
  readonly week_start: string;
  readonly inflow: number;
  readonly outflow: number;
  readonly net: number;
}

/** DSCR (Debt Service Coverage Ratio) — null when insufficient data. */
export interface DSCRMetric {
  readonly current: number;
  readonly trend_delta_30d: number;
  /** "healthy" >= 1.25; "watch" >= 1.0; "stressed" otherwise. */
  readonly interpretation: "healthy" | "watch" | "stressed";
}

/** Cash burn + runway — null when no banking data is available. */
export interface BurnRateMetric {
  readonly monthly_eur: number;
  readonly trend_delta_30d: number;
  readonly runway_months: number;
}

export interface DataCompleteness {
  readonly fic_connected: boolean;
  readonly banking_connected: boolean;
  /** 0.0–1.0 — fraction of the requested window backed by a real data source. */
  readonly coverage_pct: number;
}

/** Crediti aperti — FiC issued invoices with ``status='not_paid'``. */
export interface OpenReceivablesSummary {
  readonly total_eur_cents: number;
  readonly invoice_count: number;
  /** Invoices whose due / issue date falls within the next 30 days. */
  readonly expiring_soon_count: number;
  /** Invoices already past due by more than 60 days (Codice della Crisi). */
  readonly severely_overdue_count: number;
}

/** Debiti aperti — FiC received invoices with ``status='not_paid'``. */
export interface OpenPayablesSummary {
  readonly total_eur_cents: number;
  readonly invoice_count: number;
}

/** Pipeline SF — open Opportunities (``IsClosed=false``). */
export interface SalesforcePipelineSummary {
  readonly total_eur_cents: number;
  readonly deal_count: number;
  /** Opportunities with ``CloseDate`` within the next 30 days. */
  readonly closing_soon_count: number;
  /** Sum(Amount) of ``IsClosed && IsWon`` deals closed this calendar quarter. */
  readonly won_this_quarter_eur_cents: number;
}

/** User-supplied "Patrimonio netto" snapshot. ``null`` when missing. */
export interface EquitySnapshot {
  /** ISO date YYYY-MM-DD. */
  readonly reference_date: string;
  readonly equity_eur_cents: number;
}

/** Brand-token severity used by both indicators and deadlines. */
export type FinanceSeverity = "success" | "warning" | "danger";

/** One row of the "Adeguati Assetti · Codice Crisi" dashboard. */
export interface AdequateSetupIndicator {
  readonly label: string;
  /** Pre-formatted by the BE (e.g. "1.85", "€8.2k +12%", "€4.500 · 3"). */
  readonly value_display: string;
  /** 0..100 — controls the fill width of the row bar. */
  readonly bar_percent: number;
  readonly severity: FinanceSeverity;
}

export interface AdequateSetupResponse {
  readonly indicators: readonly AdequateSetupIndicator[];
}

export type FinancePeriod = "4w" | "13w" | "26w" | "52w";

export interface FinanceOverview {
  readonly period: FinancePeriod;
  readonly currency: string;
  readonly cash_flow: readonly CashFlowBucket[];
  readonly dscr: DSCRMetric | null;
  readonly burn_rate: BurnRateMetric | null;
  readonly as_of: string;
  readonly data_completeness: DataCompleteness;
  /** v2 extended panels — null when the source connector isn't connected. */
  readonly open_receivables: OpenReceivablesSummary | null;
  readonly open_payables: OpenPayablesSummary | null;
  readonly salesforce_pipeline: SalesforcePipelineSummary | null;
  readonly adequate_setup: AdequateSetupResponse | null;
  /**
   * Latest user-supplied equity snapshot. ``null`` when the user
   * hasn't uploaded one yet — the FE shows a "+ Aggiungi snapshot"
   * CTA instead of the Patrimonio netto indicator row.
   */
  readonly equity_snapshot: EquitySnapshot | null;
}

/** Body for ``POST /finance/balance-snapshot``. */
export interface BalanceSnapshotBody {
  /** ISO date YYYY-MM-DD (reference date of the bilancio). */
  readonly reference_date: string;
  /** Patrimonio netto in EUR cents — may be negative. */
  readonly equity_eur_cents: number;
  readonly total_assets_eur_cents?: number;
  readonly total_liabilities_eur_cents?: number;
  readonly note?: string;
}

export interface BalanceSnapshotResponse {
  readonly reference_date: string;
  readonly equity_eur_cents: number;
}

/** Upcoming fiscal/compliance deadline kinds — mirrors the BE Literal. */
export type FinanceDeadlineKind =
  | "f24"
  | "lipe"
  | "conservation"
  | "iva"
  | "other";

export type FinanceDeadlineStatus =
  | "upcoming"
  | "due_today"
  | "overdue"
  | "ready_to_submit";

export interface FinanceDeadline {
  readonly kind: FinanceDeadlineKind;
  readonly label: string;
  /** ISO date YYYY-MM-DD. */
  readonly due_date: string;
  readonly amount_eur_cents: number | null;
  readonly status: FinanceDeadlineStatus;
}

export interface FinanceDeadlinesResponse {
  readonly items: readonly FinanceDeadline[];
}

/**
 * GET ``/api/v1/finance/overview``.
 *
 * ``refresh=true`` sends ``X-Refresh-Cache: 1`` so the BE recomputes
 * instead of returning the (up to) 5-min cached payload.
 */
export async function getFinanceOverview(
  period: FinancePeriod = "13w",
  options: { refresh?: boolean } = {},
): Promise<FinanceOverview> {
  return apiClient.get<FinanceOverview>(
    `/finance/overview?period=${encodeURIComponent(period)}`,
    options.refresh ? { headers: { "X-Refresh-Cache": "1" } } : {},
  );
}

/**
 * GET ``/api/v1/finance/upcoming-deadlines``.
 *
 * Returns an empty ``items`` array when no fiscal connector is connected —
 * the FE renders a "Collega Cassetto Fiscale" placeholder rather than a
 * fake schedule.
 */
export async function getFinanceUpcomingDeadlines(
  horizonDays = 30,
): Promise<FinanceDeadlinesResponse> {
  return apiClient.get<FinanceDeadlinesResponse>(
    `/finance/upcoming-deadlines?horizon_days=${horizonDays}`,
  );
}

/**
 * POST ``/api/v1/finance/balance-snapshot`` — upsert the user's
 * Patrimonio netto value (Adeguati Assetti indicator). Re-posting the
 * same ``reference_date`` overwrites the prior value.
 */
export async function createBalanceSnapshot(
  body: BalanceSnapshotBody,
): Promise<BalanceSnapshotResponse> {
  return apiClient.post<BalanceSnapshotResponse>(
    "/finance/balance-snapshot",
    body,
  );
}

/**
 * GET ``/api/v1/finance/balance-snapshot/latest`` — most recent equity
 * snapshot, or ``null`` when none exists yet.
 */
export async function getLatestBalanceSnapshot(): Promise<
  BalanceSnapshotResponse | null
> {
  return apiClient.get<BalanceSnapshotResponse | null>(
    "/finance/balance-snapshot/latest",
  );
}
